import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  Shield, 
  Heart, 
  Clock, 
  Award,
  Target,
  ArrowRight,
  CheckCircle,
  MapPin,
  Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { siteConfig } from '../../config/site';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Hero Section
function HeroSection() {
  return (
    <section className="pt-32 pb-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p variants={fadeInUp} className="text-emerald-400 font-medium mb-4">
            About Us
          </motion.p>
          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Dedicated to Your
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400"> Safety</span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-xl text-slate-400 leading-relaxed">
            Since {siteConfig.company.founded}, {siteConfig.name} has been the trusted partner for event organizers, 
            families, and businesses seeking professional medical coverage and emergency training across Saint Lucia and the Caribbean.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// Story Section
function StorySection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
              <Calendar className="w-4 h-4" />
              <span>Our Story</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Born from a Vision for Better Healthcare
            </h2>
            <div className="text-slate-400 text-lg leading-relaxed space-y-4">
              {siteConfig.about.story.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="space-y-6">
            <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Based in Saint Lucia</h3>
                  <p className="text-slate-400 text-sm">{siteConfig.address.short}</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Proudly serving communities across Saint Lucia and the wider Caribbean region with professional medical services.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Established {siteConfig.company.founded}</h3>
                  <p className="text-slate-400 text-sm">Years of trusted service</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                What started as a small team has grown into the region&apos;s premier medical services provider.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Mission & Values Section
function MissionValues() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const valueIcons = {
    "Safety First": Shield,
    "Compassionate Care": Heart,
    "Rapid Response": Clock,
    "Excellence": Award
  };

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Mission & Vision */}
          <motion.div
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
              <Target className="w-4 h-4" />
              <span>Our Mission</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Protecting Lives, Building Confidence
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-400 text-lg leading-relaxed mb-8">
              {siteConfig.about.mission}
            </motion.p>
            
            <motion.div variants={fadeInUp} className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">Our Vision</h3>
              <p className="text-slate-400 leading-relaxed">
                {siteConfig.about.vision}
              </p>
            </motion.div>

            <motion.ul variants={fadeInUp} className="space-y-3">
              {siteConfig.company.certifications.map((cert, idx) => (
                <li key={idx} className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  {cert}
                </li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Values Grid */}
          <motion.div
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 gap-6"
          >
            {siteConfig.about.values.map((value, idx) => {
              const IconComponent = valueIcons[value.title] || Shield;
              return (
                <motion.div
                  key={idx}
                  variants={fadeInUp}
                  className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Team Section
function TeamSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} id="team" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
            <Award className="w-4 h-4" />
            <span>Leadership Team</span>
          </motion.div>
          <motion.h2 variants={fadeInUp} className="text-4xl font-bold text-white mb-4">
            Meet Our Directors
          </motion.h2>
          <motion.p variants={fadeInUp} className="max-w-2xl mx-auto text-slate-400 text-lg">
            Our leadership team brings decades of combined experience in emergency medicine, 
            healthcare operations, and paramedicine to serve Saint Lucia.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {siteConfig.directors.map((director, idx) => (
            <motion.div
              key={idx}
              variants={fadeInUp}
              whileHover={{ y: -8 }}
              className="p-8 rounded-3xl bg-slate-950 border border-slate-800 hover:border-emerald-500/30 transition-all text-center group"
            >
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6 group-hover:scale-105 transition-transform">
                {director.avatar}
              </div>
              <h3 className="text-xl font-semibold text-white mb-1">{director.name}</h3>
              <p className="text-emerald-400 text-sm font-medium mb-1">{director.role}</p>
              <p className="text-slate-500 text-xs mb-4">{director.credentials}</p>
              <p className="text-slate-400 text-sm leading-relaxed">{director.bio}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Credentials Section
function CredentialsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const credentials = siteConfig.company.certifications.map((cert, idx) => ({
    label: cert,
    icon: idx % 2 === 0 ? Award : Shield
  }));

  // Add additional credentials
  credentials.push(
    { label: 'OSHA Compliant', icon: Shield },
    { label: 'Professional Liability Coverage', icon: CheckCircle }
  );

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-white mb-4">
            Credentials & Certifications
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-slate-400 text-lg">
            We maintain the highest standards of compliance and certification.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {credentials.map((item, idx) => (
            <motion.div
              key={idx}
              variants={fadeInUp}
              className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-slate-200 font-medium">{item.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="space-y-8"
        >
          <motion.h2 variants={fadeInUp} className="text-4xl font-bold text-white">
            Ready to Work Together?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-xl text-slate-400">
            Let&apos;s discuss how we can support your event, home healthcare needs, or training goals in Saint Lucia.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Button 
              asChild
              size="lg" 
              className="px-8 py-6 text-base bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg shadow-emerald-500/25"
            >
              <Link to="/contact" data-testid="about-cta-btn">
                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <MarketingLayout>
      <HeroSection />
      <StorySection />
      <MissionValues />
      <TeamSection />
      <CredentialsSection />
      <CTASection />
    </MarketingLayout>
  );
}
